/**
 * Owner of the `atsIntegrations` localStorage mirror: every write goes
 * through the helpers here (the `AtsIntegrationsContext` fetch and the
 * login-time user-info seed) so the stored format stays uniform, and the
 * synchronous read lets components seed UI state (e.g. "does this org have
 * an ATS integrated?") without waiting on a network fetch. The authoritative
 * value still comes from the server; the read is an optimistic hint.
 *
 * The `hasJobSyncAts` hint reads the backend-supplied `supports_job_creation`
 * capability per integration directly (no provider allowlist). It exists only
 * to HIDE provider-dependent UI (the notes ATS Sync tab) and fails open on
 * unknown/missing values, so it only hides UI on a definite `false` and never
 * wrongly enables a write path. Enabling write controls should still rely on
 * the live `/field-schema` response's `supportsJobCreation`.
 */

import { useSyncExternalStore } from 'react';
import { isDefined, isRecord, parseString } from 'utils/typeGuards';
import { storage } from 'utils/typedStorage';

const ATS_INTEGRATIONS_STORAGE_KEY = 'atsIntegrations';

// Change signal: the cache is written asynchronously (integrations fetch,
// login seed), so consumers subscribe instead of reading once per mount.
const listeners = new Set<() => void>();

function emitAtsCacheChanged(): void {
  for (const listener of listeners) listener();
}

function subscribeAtsCache(listener: () => void): () => void {
  listeners.add(listener);
  // Cross-tab writes (e.g. logging in elsewhere) only surface as storage events.
  window.addEventListener('storage', listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', listener);
  };
}

interface CachedAtsIntegration {
  ats_integration_id?: string;
  ats_provider?: string;
  deleted?: boolean;
  supports_job_creation?: boolean;
}

const optionalBoolean = (value: unknown): boolean | undefined =>
  typeof value === 'boolean' ? value : undefined;

/** One stored entry, or null when it is neither an id string nor an object. */
function parseCachedIntegration(entry: unknown): CachedAtsIntegration | null {
  if (typeof entry === 'string') {
    return { ats_integration_id: entry };
  }
  if (!isRecord(entry)) {
    return null;
  }
  return {
    ats_integration_id: parseString(entry.ats_integration_id),
    ats_provider: parseString(entry.ats_provider),
    deleted: optionalBoolean(entry.deleted),
    supports_job_creation: optionalBoolean(entry.supports_job_creation),
  };
}

/** Every writer goes through here so subscribers see the removal. */
function clearCache(): void {
  storage.removeDynamic(ATS_INTEGRATIONS_STORAGE_KEY);
  emitAtsCacheChanged();
}

/**
 * Normalize and persist the cache. Accepts the two real producer shapes:
 * integration objects (the `AtsIntegrationsContext` fetch) and bare
 * integration-id strings (the login-time user-info payload). Anything else
 * clears the key so `getCachedAtsCoverage` degrades to `{ known: false }`
 * instead of reading a stale or malformed value.
 */
export function writeAtsIntegrationsCache(value: unknown): void {
  if (!Array.isArray(value)) {
    clearCache();
    return;
  }
  // Integration objects are stored whole; readers pick out the fields they use.
  const normalized: Array<CachedAtsIntegration | Record<string, unknown>> = [];
  for (const entry of value) {
    if (typeof entry === 'string') {
      normalized.push({ ats_integration_id: entry });
    } else if (isRecord(entry)) {
      normalized.push(entry);
    } else {
      clearCache();
      return;
    }
  }
  storage.setDynamic(ATS_INTEGRATIONS_STORAGE_KEY, JSON.stringify(normalized));
  emitAtsCacheChanged();
}

export interface AtsProviderCoverage {
  ats_provider?: string;
  deleted?: boolean;
}

function parseCachedIntegrations(): CachedAtsIntegration[] | null {
  try {
    const raw = storage.getDynamic(ATS_INTEGRATIONS_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.map(parseCachedIntegration).filter(isDefined);
  } catch {
    // swallow-ok: An unreadable ATS hint returns known:false; consumers keep ATS UI available and rely on live integration/schema responses for writes.
    return null;
  }
}

export interface AtsCoverageHint {
  // True when the cache is populated at all (so `hasAnyAts` is a meaningful
  // read, not an unknown state).
  known: boolean;
  // True when at least one active integration exists in the cache.
  hasAnyAts: boolean;
  // True when an active integration's backend `supports_job_creation` is not
  // explicitly false. Entries cached by older seeds lack the field; those
  // count as supported so the hint only hides UI on a definite no.
  hasJobSyncAts: boolean;
}

function getCachedAtsCoverage(): AtsCoverageHint {
  const integrations = parseCachedIntegrations();
  if (integrations === null) {
    return { known: false, hasAnyAts: false, hasJobSyncAts: false };
  }
  const active = integrations.filter((i) => !i.deleted);
  return {
    known: true,
    hasAnyAts: active.length > 0,
    hasJobSyncAts: active.some((i) => i.supports_job_creation !== false),
  };
}

// Snapshot identity must be stable: useSyncExternalStore re-renders whenever
// getSnapshot returns a new reference, so a freshly built object every call
// would loop forever. Recompute only when the stored JSON string changes.
let cachedRaw: string | null | undefined;
let cachedSnapshot: AtsCoverageHint = {
  known: false,
  hasAnyAts: false,
  hasJobSyncAts: false,
};

function getAtsCoverageSnapshot(): AtsCoverageHint {
  let raw: string | null;
  try {
    raw = storage.getDynamic(ATS_INTEGRATIONS_STORAGE_KEY);
  } catch {
    // Blocked/sandboxed storage (SecurityError). getSnapshot runs during render,
    // so a throw here would crash the note/transcript tree — return the last stable
    // snapshot instead (a stable reference, so useSyncExternalStore won't loop).
    return cachedSnapshot;
  }
  if (raw !== cachedRaw) {
    cachedRaw = raw;
    cachedSnapshot = getCachedAtsCoverage();
  }
  return cachedSnapshot;
}

/**
 * Reactive read of the ATS coverage hint. The cache lands asynchronously
 * (integrations fetch / login seed), so consumers must subscribe rather than
 * read once — a plain read can miss a write that arrives after the last render.
 */
export function useAtsCoverage(): AtsCoverageHint {
  return useSyncExternalStore(subscribeAtsCache, getAtsCoverageSnapshot);
}

/**
 * Whether the notes Sync tab should be shown for this org.
 *
 * Shown when: the cache is unknown (fail-open, so a not-yet-loaded cache
 * never wrongly hides the tab for a supported org), the org has at least one
 * ATS integration that supports job sync, or the org has no ATS integrations
 * at all (no-ATS orgs get the Tenzo "Job Sync" flow instead of ATS sync).
 * Hidden only when the org has ATS integration(s) but none of them support
 * job sync.
 */
export function shouldShowJobSyncTab(coverage: AtsCoverageHint): boolean {
  if (!coverage.known) return true;
  if (coverage.hasJobSyncAts) return true;
  return !coverage.hasAnyAts;
}

/** Active org ATS provider keys from the localStorage mirror (optimistic until SWR loads). */
export function getCachedActiveAtsProviders(): string[] {
  const integrations = parseCachedIntegrations();
  if (integrations === null) {
    return [];
  }
  return integrations
    .filter((integration) => !integration.deleted)
    .map((integration) => integration.ats_provider?.toLowerCase().trim() ?? '')
    .filter(Boolean);
}

// Provider-name allowlist: same drift risk as the removed job-sync one (an
// innova-bullhorn variant would slip through). No backend tearsheet capability
// exists yet; create one and source from it rather than extending this list.
export function orgHasTearsheetAts(
  integrations: AtsProviderCoverage[]
): boolean {
  return integrations.some((integration) => {
    if (integration.deleted) return false;
    const provider = integration.ats_provider?.toLowerCase();
    return provider === 'bullhorn' || provider === 'jobdiva';
  });
}
